import { useCallback, useContext, useMemo } from 'react';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import useConstants from '@/hooks/useConstants';
import usePrimarySubstances from '@/hooks/usePrimarySubstances';
import usePrograms from '@/hooks/usePrograms';
import PersonContext from '@/pages/Person/PersonContext';
import { programQualificationSectionSchema } from '@/pages/Profile/schemas';
import DataList, { DataRow } from '@/ui/components/DataList';

const ProgramQualificationEditor = () => {
  const {
    person,
    person: { program_qualification }
  } = useContext(PersonContext);

  const { data: constants } = useConstants();
  const selectedTreatmentModality = useMemo(() => {
    if (!program_qualification?.treatment_preference) {
      return 'None';
    }

    const treatmentPreference = constants?.data?.treatment_preference_choices?.find(
      (choice) => choice.value === program_qualification.treatment_preference
    );

    if (treatmentPreference) {
      return treatmentPreference.label;
    }

    return 'None';
  }, [constants, program_qualification?.treatment_preference]);

  const { data: primarySubstanceResponse } = usePrimarySubstances();
  const primarySubstancesForSelect = useMemo(() => {
    return (
      primarySubstanceResponse?.data?.map(({ name }) => ({
        label: name,
        value: name
      })) ?? []
    );
  }, [primarySubstanceResponse?.data]);

  const { refetch: getPrograms } = usePrograms({}, false);
  const formatPrograms = useCallback(async () => {
    const defaultProgram = { label: 'Default', value: null };
    const { data } = await getPrograms();
    if (data?.data)
      return [defaultProgram, ...data.data.map(({ id, name }) => ({ label: name, value: id }))];
    return [defaultProgram];
  }, [getPrograms]);

  return (
    <PersonInPlaceEditor
      disableEdit={!!program_qualification?.ended_at}
      sectionEditor
      schema={programQualificationSectionSchema}
      fields={[
        {
          key: 'program_qualification.id',
          label: '',
          type: FieldTypes.HIDDEN
        },
        {
          key: 'program_qualification.primary_substance',
          label: 'Primary Concern',
          type: FieldTypes.SELECT,
          options: primarySubstancesForSelect
        },
        {
          key: 'program_qualification._program_id',
          label: 'Program Family',
          type: FieldTypes.SELECT,
          options: formatPrograms,
          ...(!person.program_qualification?.selected_program &&
            person.program_qualification?.default_program && {
              additionalCaption: {
                label: 'Current default',
                value: person.program_qualification.default_program.name
              }
            })
        },
        {
          key: 'program_qualification.treatment_preference',
          label: 'Treatment Preference',
          type: FieldTypes.SELECT,
          options: constants?.data?.treatment_preference_choices || []
        }
      ]}>
      <DataList>
        {person.program_qualification ? (
          <>
            <DataRow
              label="Primary Concern"
              content={person.program_qualification.primary_substance || 'None'}
            />

            <DataRow label="Program Family">
              {person.program_qualification.selected_program
                ? person.program_qualification.selected_program.name
                : (person.program_qualification.default_program && (
                    <>
                      {person.program_qualification.default_program.name}
                      <span className="text-green-600"> (Default) </span>
                    </>
                  )) ||
                  'None'}
            </DataRow>
            <DataRow label="Treatment Preference" content={selectedTreatmentModality} />
          </>
        ) : (
          <DataRow label="None" />
        )}
      </DataList>
    </PersonInPlaceEditor>
  );
};

export default ProgramQualificationEditor;
